import PropTypes from 'prop-types';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Backdrop, Box, Button } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import UserMainSettings from './Admin/UserSettingTabs/UserMainSettings';
import UserEmailNotificationSettings from './Admin/UserSettingTabs/UserEmailNotificationSettings';
import UserChangePassword from './Admin/UserSettingTabs/UserChangePassword';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import React, { useEffect, useMemo, useState } from 'react';
import UserApiService from '../../api/UserApiService';
import TabPanel from './TabPanel';
import { UserAdminMainSettingsDto } from '../../dtos/Users/UserAdminMainSettingsDto';
import { UserAdminEmailNotificationsDto } from '../../dtos/Users/UserAdminEmailNotificationsDto';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from '../Utils/AuthUtil';
import SaveButton from '../Common/SaveButton';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import { useSnackbar } from '../Common/SnackbarProvider';

export default function UserSelfServiceModal(props) {
    const {open, onClose} = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const[loading, setLoading] = useState(true);
    const[thisUser, setThisUser] = useState();
    const[tabValue, setTabValue] = useState(0);
    const[userMainSettings, setUserMainSettings] = useState(new UserAdminMainSettingsDto());
    const[userEmailNotifications, setUserEmailNotifications] = useState(new UserAdminEmailNotificationsDto());
    const[applyNotificationChangesRetroactively, setApplyNotificationChangesRetroactively] = useState(false);
    const[firstNameError, setFirstNameError] = useState(false);
    const[lastNameError, setLastNameError] = useState(false);

    const userApiService = new UserApiService();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const userId = useMemo(() => AuthUtil.getUserId(userToken), [userToken]);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    async function loadThisUser() {
        setLoading(true);
        const userData = await userApiService.getThisUser(userId);
        if(userData.success) {
            setThisUser(userData.data);
            setUserMainSettings(userData.data.mainSettingsDto);
            setUserEmailNotifications(userData.data.emailNotificationsDto);
        } else {
            snackbar.error(t('common.errorWithMessage', { message: userData.message ?? userData }));
        }
        setLoading(false);
    }

    function a11yProps (index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    function handleChange(event, newValue) {
        setTabValue(newValue);
    }

    function handleCancel() {
        onClose();
        setTabValue(0);
    }

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        setLoading(true);
        if(validate()) {
            const updatedUser = JSON.parse(JSON.stringify(thisUser));
            updatedUser.mainSettingsDto = userMainSettings;
            updatedUser.permissionsDto = thisUser.permissionsDto;
            updatedUser.emailNotificationsDto = userEmailNotifications;
            const userUpdateResponse = await userApiService.saveUser(updatedUser);
            if (userUpdateResponse && userUpdateResponse.success) {
                snackbar.success(t('userSelfService.toast.updated'));
                onClose();
            } else {
                snackbar.error(t('userSelfService.toast.updateError', { error: userUpdateResponse.message ?? userUpdateResponse }));
            }
        }
        setLoading(false);

    });

    function validate() {
        const errors = [];
        if(!userMainSettings.usFirstname) {
            errors.push(t('userMainSettings.validation.firstNameRequired'));
        }
        setFirstNameError(!userMainSettings.usFirstname);

        if(!userMainSettings.usLastname) {
            errors.push(t('userMainSettings.validation.lastNameRequired'));
        }
        setLastNameError(!userMainSettings.usLastname);

        if(errors.length > 0) {
            snackbar.error(t('userSelfService.validation.saveFailed', { errors: errors.join(', ') }));
            return false;
        } else {
            return true;
        }
    }

    useEffect(() => {
        if(open) {
            loadThisUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return(
            <Dialog
                closeAfterTransition={true}
                sx={{ '& .MuiDialog-paper': { width: '175%' } }}
                maxWidth="lg"
                fullWidth
                open={open}
            >
                <DialogTitle>{t('userSelfService.title')}</DialogTitle>
                <DialogContent dividers>
                    {loading ? <Backdrop open={loading}><CircularProgress/></Backdrop> :
                        <Box>
                            <Box>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleChange}
                                >
                                    <Tab label={t('userTabs.mainSettings')} {...a11yProps(0)} />
                                    <Tab label={t('userTabs.emailNotifications')} {...a11yProps(1)} />
                                    <Tab label={t('userTabs.changePassword')} {...a11yProps(2)} />
                                </Tabs>
                            </Box>
                            <TabPanel
                                value={tabValue}
                                index={0}
                            >
                                <UserMainSettings
                                    userAdminMainSettings={userMainSettings}
                                    setUserAdminMainSettings={setUserMainSettings}
                                    userId={userId}
                                    isCurrentUserSuperAdmin={isSuperAdmin}
                                    firstNameError={firstNameError}
                                    lastNameError={lastNameError}
                                    editAdmin={false}
                                />
                            </TabPanel>
                            <TabPanel
                                value={tabValue}
                                index={1}
                            >
                                <UserEmailNotificationSettings
                                    userAdminEmailNotification={userEmailNotifications}
                                    setNewUserAdminEmailNotification={setUserEmailNotifications}
                                    applyNotificationChangesRetroactively={applyNotificationChangesRetroactively}
                                    setApplyNotificationChangesRetroactively={setApplyNotificationChangesRetroactively}
                                />
                            </TabPanel>
                            <TabPanel
                                value={tabValue}
                                index={2}
                            >
                                <UserChangePassword
                                    user={thisUser}
                                />
                            </TabPanel>
                        </Box>
                    }

                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        variant='contained'
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        {t('common.cancel')}
                    </Button>
                    <SaveButton
                        loading={saving}
                        variant='contained'
                        onClick={handleSave}
                        disabled={tabValue === 3 || loading}
                    >
                        {t('common.save')}
                    </SaveButton>
                </DialogActions>
            </Dialog>

    );
}

UserSelfServiceModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
};
